import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useReferrer } from '../../../hooks/useReferrer';
import { useAuthenticationApi } from '../../../services/api';
import { addIndication, INDICATION_TYPES } from '../../taboola-common-frontend-modules/Indications';
import { FormattedMessage } from '../../taboola-common-frontend-modules/i18n';

const PROFILE = 'THIRD_PARTY_CONNECT';

export const failureIndication = {
    message: <FormattedMessage id="3p-connect.error" defaultMessage="Could not connet." />,
    type: INDICATION_TYPES.ERROR,
    highlight: <FormattedMessage id="error.highlight" defaultMessage="Error!" />,
};

/**
 * Hook for handling Third Party Connect workflow
 *
 * @param {Object} selectedAccount - The selected account object
 * @returns {Function} handleSendAccountId - Function to send account ID and token to parent window
 */
export const useThirdPartyConnect = selectedAccount => {
    const dispatch = useDispatch();
    const authenticationApi = useAuthenticationApi();
    const referrer = useReferrer();

    const retrieveToken = useCallback(
        async accountId => {
            try {
                const { profiled_access_token } = await authenticationApi.generateProfiledToken({
                    accountId,
                    profile: PROFILE,
                });

                return profiled_access_token;
            } catch (error) {
                console.error('Error retrieving profiled token:', error);
                throw error;
            }
        },
        [authenticationApi]
    );

    const handleSendAccountId = useCallback(async () => {
        if (!selectedAccount || !selectedAccount.id) {
            dispatch(addIndication(failureIndication));
            return;
        }

        try {
            const profiledToken = await retrieveToken(selectedAccount.accountName);

            // Send the account ID to the parent window
            if (window.opener) {
                window.opener.postMessage(
                    {
                        type: 'ACCOUNT_SELECTED',
                        accountId: selectedAccount.id,
                        accountName: selectedAccount.accountName,
                        accountLabel: selectedAccount.label,
                        profiledToken,
                    },
                    referrer || new URL(document.referrer).origin
                );

                // Close the current window
                window.close();
            }
        } catch (error) {
            console.error('Error sending account ID:', error);
            dispatch(addIndication(failureIndication));
        }
    }, [dispatch, retrieveToken, selectedAccount, referrer]);

    return { handleSendAccountId };
};
